import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertIcon,
  AreaChartIcon,
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  ChevronRightIcon,
  CircularLoader,
  ClockIcon,
  GraphIcon,
  OpenInNewIcon,
  RefreshIcon,
  useDocumentTitle,
} from '@forge/common';
import { config } from '@onering/npm-package/web';
import { useServiceHealth } from '@/hooks/useServiceHealth';
import { useServiceSeries } from '@/hooks/useServiceSeries';
import type { ServiceHealth, ServiceStatus, ServiceSeries } from '@/api/observability';
import Sparkline from './Sparkline';

const grafanaBaseUrl = config('GRAFANA_URL') ?? '';

/** Deep-links the per-service RED dashboard (provisioned in onering-infra). */
const dashboardHref = (grafanaService: string): string =>
  grafanaBaseUrl
    ? `${grafanaBaseUrl}/d/application-observability/application-observability?var-service=${encodeURIComponent(grafanaService)}`
    : '';

/** Deep-links the shared Infrastructure Health dashboard (postgres + oracle). */
const infraDashboardHref = (): string =>
  grafanaBaseUrl ? `${grafanaBaseUrl}/d/infra-health/infrastructure-health` : '';

const statusMeta: Record<
  ServiceStatus,
  { label: string; core: string; ring: string; badge: 'success' | 'warning' | 'error' | 'neutral' }
> = {
  operational: { label: 'Operational', core: 'bg-green-500', ring: 'bg-green-400', badge: 'success' },
  degraded: { label: 'Degraded', core: 'bg-yellow-500', ring: 'bg-yellow-400', badge: 'warning' },
  down: { label: 'Down', core: 'bg-red-500', ring: 'bg-red-400', badge: 'error' },
  unknown: { label: 'Unknown', core: 'bg-gray-400', ring: 'bg-gray-300', badge: 'neutral' },
};

const statusRank: Record<ServiceStatus, number> = {
  operational: 0,
  unknown: 1,
  degraded: 2,
  down: 3,
};

const fmtPct = (n: number | null): string => (n === null ? '—' : `${n.toFixed(2)}%`);
const fmtMs = (n: number | null): string => (n === null ? '—' : `${n} ms`);

const StatusPulse = ({ status, size = 'sm' }: { status: ServiceStatus; size?: 'sm' | 'lg' }) => {
  const meta = statusMeta[status];
  const dim = size === 'lg' ? 'h-3 w-3' : 'h-2.5 w-2.5';
  return (
    <span className={`relative inline-flex ${dim} shrink-0`} aria-hidden="true">
      <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${meta.ring}`} />
      <span className={`relative inline-flex ${dim} rounded-full ${meta.core}`} />
    </span>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0">
    <p className="text-xs text-color-tertiary">{label}</p>
    <p className="text-sm font-semibold tabular-nums">{value}</p>
  </div>
);

const StatCard = ({ label, value }: { label: string; value: string }) => (
  <Card>
    <CardContent className="flex flex-col gap-1">
      <p className="text-xs text-color-tertiary">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
    </CardContent>
  </Card>
);

const StatusCount = ({ status, count }: { status: ServiceStatus; count: number }) => {
  const meta = statusMeta[status];
  return (
    <div className="flex items-center gap-2">
      <span className={`h-2.5 w-2.5 rounded-full ${meta.core}`} aria-hidden="true" />
      <span className="text-sm font-semibold tabular-nums">{count}</span>
      <span className="text-xs text-color-tertiary">{meta.label}</span>
    </div>
  );
};

const TrendCell = ({
  label,
  series,
  loading,
  colorClass,
}: {
  label: string;
  series: ServiceSeries | undefined;
  loading: boolean;
  colorClass: string;
}) => (
  <div className="min-w-0">
    <p className="mb-1 text-[11px] text-color-tertiary">{label}</p>
    {loading && !series ? (
      <div className="h-7 w-full animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
    ) : (
      <Sparkline points={series?.points ?? []} colorClass={colorClass} ariaLabel={label} />
    )}
  </div>
);

/** 24h latency + error-rate trends, lazily fetched per card. */
const ServiceTrends = ({ serviceId }: { serviceId: string }) => {
  const latency = useServiceSeries(serviceId, 'latency');
  const errors = useServiceSeries(serviceId, 'errorRate');
  return (
    <div className="grid grid-cols-2 gap-3">
      <TrendCell
        label="p95 latency · 24h"
        series={latency.series}
        loading={latency.isLoading}
        colorClass="text-blue-500 dark:text-blue-400"
      />
      <TrendCell
        label="Error rate · 24h"
        series={errors.series}
        loading={errors.isLoading}
        colorClass="text-red-500 dark:text-red-400"
      />
    </div>
  );
};

const ServiceCard = ({
  svc,
  withTrends = false,
  showDashboard = true,
}: {
  svc: ServiceHealth;
  withTrends?: boolean;
  showDashboard?: boolean;
}) => {
  const meta = statusMeta[svc.status];
  const href = showDashboard ? dashboardHref(svc.grafanaService) : '';
  const footerText =
    svc.status !== 'operational' && svc.reason
      ? svc.reason
      : svc.requestsPerMin !== null
        ? `${svc.requestsPerMin.toLocaleString()} req/min`
        : 'Live health probe';
  return (
    <Card className="h-full">
      <CardContent className="flex h-full flex-col gap-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <StatusPulse status={svc.status} />
              <p className="truncate font-semibold">{svc.name}</p>
            </div>
            <p className="mt-0.5 text-xs text-color-tertiary">{svc.category}</p>
          </div>
          <Badge type={meta.badge}>{meta.label}</Badge>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Metric label="Uptime (30d)" value={fmtPct(svc.uptime)} />
          {svc.latencyMs !== null ? (
            <Metric label="p95 latency" value={fmtMs(svc.latencyMs)} />
          ) : (
            <Metric label="Response" value={fmtMs(svc.responseMs)} />
          )}
          <Metric label="Error rate" value={fmtPct(svc.errorRate)} />
        </div>

        {withTrends && <ServiceTrends serviceId={svc.id} />}

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-200 pt-3 dark:border-gray-800">
          <span
            className={`truncate text-xs tabular-nums ${svc.status !== 'operational' && svc.reason ? 'text-color-secondary dark:text-color-secondary-dark' : 'text-color-tertiary'}`}
            title={footerText}
          >
            {footerText}
          </span>
          {href ? (
            <Button
              as="link"
              href={href}
              variant="quiet-primary"
              target="_blank"
              rel="noopener noreferrer"
              external
            >
              View dashboard
            </Button>
          ) : showDashboard ? (
            <Button variant="quiet-primary" disabled>
              View dashboard
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Infrastructure dependency card. Unlike a service it links the shared
 * Infrastructure Health dashboard, and — when the dependency supports a
 * per-instance drill-down (Oracle) — offers a "View all" button that opens the
 * dedicated databases page.
 */
const InfraCard = ({
  svc,
  onViewAll,
}: {
  svc: ServiceHealth;
  onViewAll?: () => void;
}) => {
  const meta = statusMeta[svc.status];
  const href = infraDashboardHref();
  const footerText =
    svc.status !== 'operational' && svc.reason ? svc.reason : 'Exporter-derived health';
  return (
    <Card className="h-full">
      <CardContent className="flex h-full flex-col gap-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <StatusPulse status={svc.status} />
              <p className="truncate font-semibold">{svc.name}</p>
            </div>
            <p className="mt-0.5 text-xs text-color-tertiary">{svc.category}</p>
          </div>
          <Badge type={meta.badge}>{meta.label}</Badge>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-200 pt-3 dark:border-gray-800">
          <span
            className={`truncate text-xs ${svc.status !== 'operational' && svc.reason ? 'text-color-secondary dark:text-color-secondary-dark' : 'text-color-tertiary'}`}
            title={footerText}
          >
            {footerText}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            {onViewAll && (
              <Button
                variant="quiet-primary"
                icon={<ChevronRightIcon />}
                iconPosition="right"
                onClick={onViewAll}
              >
                View all
              </Button>
            )}
            {href ? (
              <Button
                as="link"
                href={href}
                variant="quiet-primary"
                icon={<OpenInNewIcon />}
                target="_blank"
                rel="noopener noreferrer"
                external
              >
                View dashboard
              </Button>
            ) : (
              <Button variant="quiet-primary" icon={<OpenInNewIcon />} disabled>
                View dashboard
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const quickLinks = [
  {
    title: 'Dashboards',
    description: 'Browse service, latency, and resource-utilization dashboards.',
    icon: <GraphIcon />,
    path: '',
  },
  {
    title: 'Metrics & Traces',
    description: 'Explore OpenTelemetry traces and metrics across request flows.',
    icon: <AreaChartIcon />,
    path: '/explore',
  },
  {
    title: 'Alerts & Incidents',
    description: 'Review alert rules and triage active incidents.',
    icon: <AlertIcon />,
    path: '/alerting/list',
  },
];

const ObservabilityPage = () => {
  useDocumentTitle('Observability — OneRing Portal');
  const navigate = useNavigate();

  const { snapshot, isLoading, error, reload } = useServiceHealth();
  const services = useMemo(() => snapshot?.services ?? [], [snapshot]);
  const infrastructure = useMemo(() => snapshot?.infrastructure ?? [], [snapshot]);

  const summary = useMemo(() => {
    const counts: Record<ServiceStatus, number> = {
      operational: 0,
      degraded: 0,
      down: 0,
      unknown: 0,
    };
    let worst: ServiceStatus = 'operational';
    let uptimeSum = 0;
    let uptimeCount = 0;
    let latencySum = 0;
    let latencyCount = 0;
    let errSum = 0;
    let errCount = 0;

    for (const s of services) {
      counts[s.status] += 1;
      if (statusRank[s.status] > statusRank[worst]) worst = s.status;
      if (s.uptime !== null) {
        uptimeSum += s.uptime;
        uptimeCount += 1;
      }
      if (s.latencyMs !== null) {
        latencySum += s.latencyMs;
        latencyCount += 1;
      }
      if (s.errorRate !== null) {
        errSum += s.errorRate;
        errCount += 1;
      }
    }

    return {
      counts,
      worst,
      total: services.length,
      avgUptime: uptimeCount ? uptimeSum / uptimeCount : null,
      avgLatency: latencyCount ? Math.round(latencySum / latencyCount) : null,
      avgError: errCount ? errSum / errCount : null,
    };
  }, [services]);

  const overallLabel =
    summary.worst === 'operational'
      ? 'All systems operational'
      : summary.worst === 'degraded'
        ? 'Some services degraded'
        : summary.worst === 'down'
          ? 'Service outage detected'
          : 'Status unknown';

  const updatedAt = snapshot
    ? new Date(snapshot.updatedAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">Observability</h2>
          <p className="mt-1 max-w-2xl text-sm text-color-tertiary">
            A quick glance at OneRing platform health. Dashboards, alerting, and deep dives are
            configured directly in Grafana.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            icon={<RefreshIcon />}
            onClick={() => reload()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          {grafanaBaseUrl ? (
            <Button
              as="link"
              href={grafanaBaseUrl}
              variant="primary"
              icon={<OpenInNewIcon />}
              target="_blank"
              rel="noopener noreferrer"
              external
            >
              Open Grafana
            </Button>
          ) : (
            <Button variant="primary" icon={<OpenInNewIcon />} disabled>
              Open Grafana
            </Button>
          )}
        </div>
      </div>

      {error && !snapshot && (
        <Banner
          variant="inline"
          type="error"
          title="Failed to load service health"
          description={error}
          canClose={false}
        />
      )}

      {snapshot?.stale && (
        <Banner
          variant="inline"
          type="warning"
          title="Showing last known status"
          description="The latest health refresh did not complete; values below may be out of date."
          canClose={false}
        />
      )}

      {isLoading && !snapshot ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
          <CircularLoader size="large" />
          <p className="text-sm text-color-tertiary">Checking service health…</p>
        </div>
      ) : (
        <>
          {/* Overall status banner */}
          <Card>
            <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <StatusPulse status={summary.worst} size="lg" />
                <div>
                  <p className="text-base font-semibold">{overallLabel}</p>
                  <span className="mt-0.5 flex items-center gap-1 text-xs text-color-tertiary">
                    <ClockIcon /> Updated {updatedAt} · {summary.counts.operational} of{' '}
                    {summary.total} services operational
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <StatusCount status="operational" count={summary.counts.operational} />
                <StatusCount status="degraded" count={summary.counts.degraded} />
                <StatusCount status="down" count={summary.counts.down} />
                {summary.counts.unknown > 0 && (
                  <StatusCount status="unknown" count={summary.counts.unknown} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Aggregate metric tiles */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard
              label="Avg uptime (30d)"
              value={summary.avgUptime !== null ? `${summary.avgUptime.toFixed(2)}%` : '—'}
            />
            <StatCard
              label="Avg p95 latency"
              value={summary.avgLatency !== null ? `${summary.avgLatency} ms` : '—'}
            />
            <StatCard
              label="Avg error rate"
              value={summary.avgError !== null ? `${summary.avgError.toFixed(2)}%` : '—'}
            />
            <StatCard label="Services down" value={String(summary.counts.down)} />
          </div>

          {/* Service health grid */}
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-color-tertiary">
              Service health
            </h3>
            {services.length === 0 ? (
              <Card>
                <CardContent>
                  <p className="text-sm text-color-tertiary">
                    No monitored services are configured yet.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {services.map((svc) => (
                  <ServiceCard key={svc.id} svc={svc} withTrends />
                ))}
              </div>
            )}
          </section>

          {/* Infrastructure health */}
          {infrastructure.length > 0 && (
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-color-tertiary">
                Infrastructure
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {infrastructure.map((infra) => (
                  <InfraCard
                    key={infra.id}
                    svc={infra}
                    onViewAll={
                      infra.id === 'oracle'
                        ? () => navigate('/observability/oracle')
                        : infra.id === 'mssql'
                          ? () => navigate('/observability/azure-sql')
                          : undefined
                    }
                  />
                ))}
              </div>
            </section>
          )}

          {/* Explore in Grafana */}
          <section>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-color-tertiary">
              Explore in Grafana
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {quickLinks.map((link) => {
                const linkHref = grafanaBaseUrl ? `${grafanaBaseUrl}${link.path}` : '';
                const card = (
                  <Card className="h-full">
                    <CardContent className="flex h-full items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-100">
                        {link.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold">{link.title}</p>
                          <ChevronRightIcon className="shrink-0 text-color-tertiary" />
                        </div>
                        <p className="mt-1 text-sm text-color-tertiary">{link.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
                return linkHref ? (
                  <a
                    key={link.title}
                    href={linkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    {card}
                  </a>
                ) : (
                  <div key={link.title} className="opacity-60">
                    {card}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ObservabilityPage;
